/**
 * Mist Error Code Catalog — stable, machine-readable error identifiers.
 *
 * Families:
 *   MIST_P0xxx  — Parse / syntax
 *   MIST_T1xxx  — Type / semantic
 *   MIST_C2xxx  — Contract / structure
 *   MIST_V3xxx  — Value-flow / PVI
 *   MIST_D4xxx  — Determinism / policy
 *   MIST_B5xxx  — Build / bundle
 *   MIST_DP6xxx — Deploy preflight
 *   MIST_S7xxx  — Simulation
 *   MIST_A8xxx  — Adversarial
 *   MIST_I9xxx  — Internal
 *
 * Every code must be stable. Do not rename or reassign codes.
 */

import type {
  MistDiagnosticCategory,
  MistDiagnosticStage,
  MistDiagnosticSeverity,
} from './diagnosticTypes';

/* ================================================================== */
/*  Code definition                                                   */
/* ================================================================== */

export interface MistErrorCodeDef {
  code: string;
  title: string;
  category: MistDiagnosticCategory;
  stage: MistDiagnosticStage;
  defaultSeverity: MistDiagnosticSeverity;
  /** Whether this code blocks deployment by default. */
  deployBlocking: boolean;
  /** Whether this code blocks simulation by default. */
  simulationBlocking: boolean;
  /** Whether this code blocks PVI graph by default. */
  graphBlocking: boolean;
}

function def(
  code: string,
  title: string,
  category: MistDiagnosticCategory,
  stage: MistDiagnosticStage,
  severity: MistDiagnosticSeverity,
  flags: { deploy?: boolean; sim?: boolean; graph?: boolean } = {},
): MistErrorCodeDef {
  const isError = severity === 'Error';
  return {
    code,
    title,
    category,
    stage,
    defaultSeverity: severity,
    deployBlocking: flags.deploy ?? isError,
    simulationBlocking: flags.sim ?? (isError && ['Lex', 'Parse', 'TypeCheck', 'Normalize'].includes(stage)),
    graphBlocking: flags.graph ?? (isError && ['Lex', 'Parse', 'TypeCheck'].includes(stage)),
  };
}

/* ================================================================== */
/*  Parse / Syntax — MIST_P0xxx                                      */
/* ================================================================== */

export const MIST_P0001 = def('MIST_P0001', 'UnexpectedToken', 'Syntax', 'Parse', 'Error');
export const MIST_P0002 = def('MIST_P0002', 'InvalidIndentation', 'Syntax', 'Lex', 'Error');
export const MIST_P0003 = def('MIST_P0003', 'MissingIndentedBlock', 'Syntax', 'Parse', 'Error');
export const MIST_P0004 = def('MIST_P0004', 'InvalidBlockClosure', 'Syntax', 'Parse', 'Error');
export const MIST_P0005 = def('MIST_P0005', 'UnterminatedString', 'Syntax', 'Lex', 'Error');
export const MIST_P0006 = def('MIST_P0006', 'InvalidLiteral', 'Syntax', 'Lex', 'Error');
export const MIST_P0007 = def('MIST_P0007', 'InvalidKeywordPlacement', 'Syntax', 'Parse', 'Error');
export const MIST_P0008 = def('MIST_P0008', 'MissingColon', 'Syntax', 'Parse', 'Error');
export const MIST_P0009 = def('MIST_P0009', 'MixedIndentationMode', 'Syntax', 'Lex', 'Error');
export const MIST_P0010 = def('MIST_P0010', 'DuplicateTopLevelConstruct', 'Syntax', 'Parse', 'Error');
export const MIST_P0011 = def('MIST_P0011', 'UnexpectedCharacter', 'Syntax', 'Lex', 'Error');
export const MIST_P0012 = def('MIST_P0012', 'InconsistentDedent', 'Syntax', 'Lex', 'Error');
export const MIST_P0013 = def('MIST_P0013', 'EmptySourceFile', 'Syntax', 'Lex', 'Info', { deploy: false, sim: false, graph: false });

/* ================================================================== */
/*  Type / Semantic — MIST_T1xxx                                     */
/* ================================================================== */

export const MIST_T1001 = def('MIST_T1001', 'UnknownIdentifier', 'NameResolution', 'TypeCheck', 'Error');
export const MIST_T1002 = def('MIST_T1002', 'DuplicateIdentifier', 'NameResolution', 'TypeCheck', 'Error');
export const MIST_T1003 = def('MIST_T1003', 'InvalidTypeUsage', 'Type', 'TypeCheck', 'Error');
export const MIST_T1004 = def('MIST_T1004', 'InvalidFunctionSignature', 'Type', 'TypeCheck', 'Error');
export const MIST_T1005 = def('MIST_T1005', 'InvalidStateDeclaration', 'Type', 'TypeCheck', 'Error');
export const MIST_T1006 = def('MIST_T1006', 'InvalidArgumentShape', 'Type', 'TypeCheck', 'Error');
export const MIST_T1007 = def('MIST_T1007', 'InvalidOperationTarget', 'Type', 'TypeCheck', 'Error');
export const MIST_T1008 = def('MIST_T1008', 'InvalidConditionType', 'Type', 'TypeCheck', 'Error');
export const MIST_T1009 = def('MIST_T1009', 'InvalidReturnType', 'Type', 'TypeCheck', 'Error');
export const MIST_T1010 = def('MIST_T1010', 'ContractRootMissing', 'ContractStructure', 'Parse', 'Error');
export const MIST_T1011 = def('MIST_T1011', 'MissingCondition', 'Type', 'TypeCheck', 'Error');
export const MIST_T1012 = def('MIST_T1012', 'MissingEventName', 'Type', 'TypeCheck', 'Error');
export const MIST_T1013 = def('MIST_T1013', 'MissingInitializer', 'Type', 'TypeCheck', 'Error');
export const MIST_T1014 = def('MIST_T1014', 'ReturnOutsideFunction', 'Type', 'TypeCheck', 'Error');

/* ================================================================== */
/*  Bounded Collections — MIST_T1015-T1020                           */
/* ================================================================== */

export const MIST_T1015 = def('MIST_T1015', 'BoundedCollectionMissingCapacity', 'Type', 'TypeCheck', 'Error');
export const MIST_T1016 = def('MIST_T1016', 'BoundedCollectionInvalidCapacity', 'Type', 'TypeCheck', 'Error');
export const MIST_T1017 = def('MIST_T1017', 'BoundedSetIterationForbidden', 'Type', 'TypeCheck', 'Error');
export const MIST_T1018 = def('MIST_T1018', 'BoundedMapIterationForbidden', 'Type', 'TypeCheck', 'Error');
export const MIST_T1019 = def('MIST_T1019', 'UnprovableLoopBound', 'Type', 'TypeCheck', 'Error');
export const MIST_T1020 = def('MIST_T1020', 'LoopBoundExceedsComplexityBudget', 'Type', 'TypeCheck', 'Error');

/* ================================================================== */
/*  Contract / Structure — MIST_C2xxx                                */
/* ================================================================== */

export const MIST_C2001 = def('MIST_C2001', 'MissingContractDeclaration', 'ContractStructure', 'Parse', 'Error');
export const MIST_C2002 = def('MIST_C2002', 'MultipleContractRootsUnsupported', 'ContractStructure', 'Parse', 'Error');
export const MIST_C2003 = def('MIST_C2003', 'InvalidEventDeclaration', 'ContractStructure', 'Parse', 'Error');
export const MIST_C2004 = def('MIST_C2004', 'InvalidPolicyDeclaration', 'ContractStructure', 'Parse', 'Error');
export const MIST_C2005 = def('MIST_C2005', 'UnsupportedTopLevelConstruct', 'ContractStructure', 'Parse', 'Error');
export const MIST_C2006 = def('MIST_C2006', 'EmptyDeployableContract', 'ContractStructure', 'StaticAnalysis', 'Warning', { deploy: false });

/* ================================================================== */
/*  Value-flow / PVI — MIST_V3xxx                                   */
/* ================================================================== */

export const MIST_V3001 = def('MIST_V3001', 'HoldWithoutResolution', 'ValueFlow', 'StaticAnalysis', 'Warning', { deploy: true, sim: false, graph: false });
export const MIST_V3002 = def('MIST_V3002', 'SplitTotalMismatch', 'ValueFlow', 'StaticAnalysis', 'Error', { sim: true });
export const MIST_V3003 = def('MIST_V3003', 'NegativeOrInvalidValueFlow', 'ValueFlow', 'StaticAnalysis', 'Error');
export const MIST_V3004 = def('MIST_V3004', 'InvalidMintAuthority', 'ValueFlow', 'StaticAnalysis', 'Warning', { deploy: true });
export const MIST_V3005 = def('MIST_V3005', 'InvalidBurnSource', 'ValueFlow', 'StaticAnalysis', 'Error');
export const MIST_V3006 = def('MIST_V3006', 'UnreachableValueNode', 'ValueFlow', 'StaticAnalysis', 'Warning', { deploy: false });
export const MIST_V3007 = def('MIST_V3007', 'IncompleteSettlementPath', 'ValueFlow', 'StaticAnalysis', 'Warning', { deploy: true });
export const MIST_V3008 = def('MIST_V3008', 'UnsupportedValueOperationOrdering', 'ValueFlow', 'StaticAnalysis', 'Error');
export const MIST_V3009 = def('MIST_V3009', 'ValueConservationViolation', 'ValueFlow', 'StaticAnalysis', 'Error');
export const MIST_V3010 = def('MIST_V3010', 'DanglingRefundOrReleaseReference', 'ValueFlow', 'StaticAnalysis', 'Warning', { deploy: true });
export const MIST_V3011 = def('MIST_V3011', 'PayWithoutDestination', 'ValueFlow', 'StaticAnalysis', 'Warning', { deploy: true });
export const MIST_V3012 = def('MIST_V3012', 'HoldWithoutSource', 'ValueFlow', 'StaticAnalysis', 'Warning', { deploy: true });
export const MIST_V3013 = def('MIST_V3013', 'SplitNoTargets', 'ValueFlow', 'StaticAnalysis', 'Error');

/* ================================================================== */
/*  Determinism / Policy — MIST_D4xxx                                */
/* ================================================================== */

export const MIST_D4001 = def('MIST_D4001', 'NonDeterministicConstruct', 'Determinism', 'StaticAnalysis', 'Error');
export const MIST_D4002 = def('MIST_D4002', 'InvalidOracleUsage', 'Determinism', 'StaticAnalysis', 'Error');
export const MIST_D4003 = def('MIST_D4003', 'InvalidTimeUsage', 'Determinism', 'StaticAnalysis', 'Warning', { deploy: false });
export const MIST_D4004 = def('MIST_D4004', 'UnsupportedExternalDependency', 'Determinism', 'StaticAnalysis', 'Error');
export const MIST_D4005 = def('MIST_D4005', 'UnsupportedDynamicBehavior', 'Determinism', 'StaticAnalysis', 'Error');

/* ================================================================== */
/*  Build / Bundle — MIST_B5xxx                                      */
/* ================================================================== */

export const MIST_B5001 = def('MIST_B5001', 'BundleGenerationFailed', 'Build', 'BundleBuild', 'Error');
export const MIST_B5002 = def('MIST_B5002', 'ArtifactMissing', 'Build', 'BundleBuild', 'Error');
export const MIST_B5003 = def('MIST_B5003', 'ArtifactCorrupted', 'Build', 'BundleBuild', 'Error');
export const MIST_B5004 = def('MIST_B5004', 'PviEmissionFailed', 'Build', 'PviLowering', 'Error');
export const MIST_B5005 = def('MIST_B5005', 'CompilerPipelineFailed', 'Build', 'BundleBuild', 'Error');

/* ================================================================== */
/*  Deploy Preflight — MIST_DP6xxx                                   */
/* ================================================================== */

export const MIST_DP6001 = def('MIST_DP6001', 'NonMistDeploySource', 'DeployPreflight', 'DeployPreflight', 'Error', { sim: false, graph: false });
export const MIST_DP6002 = def('MIST_DP6002', 'NoDeployableContractFound', 'DeployPreflight', 'DeployPreflight', 'Error', { sim: false, graph: false });
export const MIST_DP6003 = def('MIST_DP6003', 'MultipleDeployTargetsNeedSelection', 'DeployPreflight', 'DeployPreflight', 'Warning', { deploy: false, sim: false, graph: false });
export const MIST_DP6004 = def('MIST_DP6004', 'WalletNotConnected', 'DeployPreflight', 'DeployPreflight', 'Warning', { deploy: true, sim: false, graph: false });
export const MIST_DP6005 = def('MIST_DP6005', 'SessionUnavailable', 'DeployPreflight', 'DeployPreflight', 'Warning', { deploy: true, sim: false, graph: false });
export const MIST_DP6006 = def('MIST_DP6006', 'BuildRequiredBeforeDeploy', 'DeployPreflight', 'DeployPreflight', 'Error', { sim: false, graph: false });
export const MIST_DP6007 = def('MIST_DP6007', 'NetworkConfigurationMissing', 'DeployPreflight', 'DeployPreflight', 'Error', { sim: false, graph: false });
export const MIST_DP6008 = def('MIST_DP6008', 'DeployBlockedByErrors', 'DeployPreflight', 'DeployPreflight', 'Error', { sim: false, graph: false });

/* ================================================================== */
/*  Simulation — MIST_S7xxx                                          */
/* ================================================================== */

export const MIST_S7001 = def('MIST_S7001', 'SimulationInputMissing', 'Simulation', 'Simulation', 'Error', { deploy: false, graph: false });
export const MIST_S7002 = def('MIST_S7002', 'SimulationBlockedByCompileErrors', 'Simulation', 'Simulation', 'Error', { deploy: false, graph: false });
export const MIST_S7003 = def('MIST_S7003', 'SimulationStateInvalid', 'Simulation', 'Simulation', 'Error', { deploy: false, graph: false });
export const MIST_S7004 = def('MIST_S7004', 'SimulationValueFailure', 'Simulation', 'Simulation', 'Warning', { deploy: false, sim: false, graph: false });
export const MIST_S7005 = def('MIST_S7005', 'SimulationConditionFailure', 'Simulation', 'Simulation', 'Warning', { deploy: false, sim: false, graph: false });

/* ================================================================== */
/*  Adversarial — MIST_A8xxx                                         */
/* ================================================================== */

export const MIST_A8001 = def('MIST_A8001', 'AdversarialScenarioInvalid', 'Adversarial', 'Adversarial', 'Error', { deploy: false, sim: false, graph: false });
export const MIST_A8002 = def('MIST_A8002', 'AdversarialRunFailed', 'Adversarial', 'Adversarial', 'Warning', { deploy: false, sim: false, graph: false });
export const MIST_A8003 = def('MIST_A8003', 'AdversarialRiskDetected', 'Adversarial', 'Adversarial', 'Warning', { deploy: true, sim: false, graph: false });

/* ================================================================== */
/*  Internal — MIST_I9xxx                                            */
/* ================================================================== */

export const MIST_I9001 = def('MIST_I9001', 'UnexpectedCompilerState', 'Internal', 'Unknown', 'Error', { sim: false, graph: false });
export const MIST_I9002 = def('MIST_I9002', 'SpanMappingFailure', 'Internal', 'Unknown', 'Warning', { deploy: false, sim: false, graph: false });
export const MIST_I9003 = def('MIST_I9003', 'DiagnosticConversionFailure', 'Internal', 'Unknown', 'Warning', { deploy: false, sim: false, graph: false });

/* ================================================================== */
/*  Lookup                                                           */
/* ================================================================== */

export const ALL_ERROR_CODES: Record<string, MistErrorCodeDef> = {
  MIST_P0001, MIST_P0002, MIST_P0003, MIST_P0004, MIST_P0005,
  MIST_P0006, MIST_P0007, MIST_P0008, MIST_P0009, MIST_P0010,
  MIST_P0011, MIST_P0012, MIST_P0013,
  MIST_T1001, MIST_T1002, MIST_T1003, MIST_T1004, MIST_T1005,
  MIST_T1006, MIST_T1007, MIST_T1008, MIST_T1009, MIST_T1010,
  MIST_T1011, MIST_T1012, MIST_T1013, MIST_T1014, MIST_T1015,
  MIST_T1016, MIST_T1017, MIST_T1018, MIST_T1019, MIST_T1020,
  MIST_C2001, MIST_C2002, MIST_C2003, MIST_C2004, MIST_C2005, MIST_C2006,
  MIST_V3001, MIST_V3002, MIST_V3003, MIST_V3004, MIST_V3005,
  MIST_V3006, MIST_V3007, MIST_V3008, MIST_V3009, MIST_V3010,
  MIST_V3011, MIST_V3012, MIST_V3013,
  MIST_D4001, MIST_D4002, MIST_D4003, MIST_D4004, MIST_D4005,
  MIST_B5001, MIST_B5002, MIST_B5003, MIST_B5004, MIST_B5005,
  MIST_DP6001, MIST_DP6002, MIST_DP6003, MIST_DP6004, MIST_DP6005,
  MIST_DP6006, MIST_DP6007, MIST_DP6008,
  MIST_S7001, MIST_S7002, MIST_S7003, MIST_S7004, MIST_S7005,
  MIST_A8001, MIST_A8002, MIST_A8003,
  MIST_I9001, MIST_I9002, MIST_I9003,
};

/** Lookup error code definition by code string. */
export function lookupCode(code: string): MistErrorCodeDef | undefined {
  return ALL_ERROR_CODES[code];
}

/* ================================================================== */
/*  Old-code → New-code migration map                                */
/* ================================================================== */

/**
 * Maps legacy error codes from the old mistLanguageService to new
 * stable codes. Used during the transition period.
 */
export const LEGACY_CODE_MAP: Record<string, string> = {
  'MIST_TAB': 'MIST_P0009',
  'MIST_INDENT': 'MIST_P0002',
  'MIST_DEDENT': 'MIST_P0012',
  'MIST_STRING': 'MIST_P0005',
  'MIST_NUMBER': 'MIST_P0006',
  'MIST_UNEXPECTED_CHAR': 'MIST_P0011',
  'MIST_EXPECTED': 'MIST_P0001',
  'MIST_EXPECTED_INDENT': 'MIST_P0003',
  'MIST_NO_CONTRACT': 'MIST_C2001',
  'MIST_NO_CONTRACT_ROOT': 'MIST_T1010',
  'MIST_MULTIPLE_CONTRACTS': 'MIST_C2002',
  'MIST_UNEXPECTED': 'MIST_P0007',
  'MIST_INVALID_TYPE': 'MIST_T1003',
  'MIST_DUPLICATE_STATE': 'MIST_T1002',
  'MIST_DUPLICATE_FUNC': 'MIST_T1002',
  'MIST_DUPLICATE_EVENT': 'MIST_T1002',
  'MIST_NO_FUNCTIONS': 'MIST_C2006',
  'MIST_E_SPLIT_PERCENTAGE': 'MIST_V3002',
  'MIST_MISSING_CONDITION': 'MIST_T1011',
  'MIST_MISSING_EVENT': 'MIST_T1012',
  'MIST_LET_NO_INIT': 'MIST_T1013',
  'MIST_PAY_NO_DEST': 'MIST_V3011',
  'MIST_HOLD_NO_FROM': 'MIST_V3012',
  'MIST_RETURN_OUTSIDE': 'MIST_T1014',
  'MIST_EMPTY': 'MIST_P0013',
  'MIST_NOT_DEPLOYABLE': 'MIST_DP6001',
  'MIST_NO_ENTRY': 'MIST_DP6002',
  'MIST_NO_WALLET': 'MIST_DP6004',
  'MIST_NO_ACCOUNT': 'MIST_DP6005',
  // PVI graph warnings
  'MIST_E_LOCKED_FUNDS': 'MIST_V3001',
  'MIST_E_SPLIT_SUM': 'MIST_V3013',
  'MIST_E_MINT_AUTHORITY': 'MIST_V3004',
  'MIST_E_UNREACHABLE': 'MIST_V3006',
};

/* ================================================================== */
/*  Category / Stage label maps (for UI)                             */
/* ================================================================== */

export const CATEGORY_LABELS: Record<string, string> = {
  Syntax: 'Syntax',
  ParseStructure: 'Parse',
  Type: 'Type',
  NameResolution: 'Name',
  ContractStructure: 'Contract',
  ValueFlow: 'ValueFlow',
  Determinism: 'Determinism',
  Policy: 'Policy',
  PviLowering: 'PVI',
  UvlInvariant: 'UVL',
  Build: 'Build',
  DeployPreflight: 'Deploy',
  Simulation: 'Simulation',
  Adversarial: 'Adversarial',
  Internal: 'Internal',
};

export const STAGE_LABELS: Record<string, string> = {
  Lex: 'Lexer',
  Parse: 'Parser',
  TypeCheck: 'TypeCheck',
  Normalize: 'Normalize',
  StaticAnalysis: 'Analysis',
  PviLowering: 'PVI',
  BundleBuild: 'Build',
  DeployPreflight: 'Deploy',
  Simulation: 'Simulation',
  Adversarial: 'Adversarial',
  Runtime: 'Runtime',
  Unknown: 'Unknown',
};
